import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { ArrowRight, Building2, FileText, Check, Mail, HelpCircle } from "lucide-react";

const Onboarding = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Business info
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [businessInitials, setBusinessInitials] = useState(user?.business_initials || "");
  const [businessAddress, setBusinessAddress] = useState(user?.business_address || "");
  const [replyEmail, setReplyEmail] = useState(user?.custom_sender_email || user?.email || "");

  const handleStep1 = async () => {
    if (!businessName.trim()) return;
    setSaving(true);
    try {
      const initials = businessInitials || businessName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
      const updateData = {
        business_name: businessName,
        business_initials: initials,
        business_address: businessAddress,
      };
      if (replyEmail.trim() && replyEmail !== user?.email) {
        updateData.custom_sender_email = replyEmail.trim();
      }
      const { data } = await axios.put(`${API}/user/settings`, updateData);
      setUser(prev => ({ ...prev, ...data }));
      setStep(2);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvoice = () => {
    navigate("/invoices/new");
  };

  const handleComplete = async () => {
    try {
      await axios.post(`${API}/auth/complete-onboarding`);
      setUser(prev => ({ ...prev, onboarding_complete: true }));
      navigate("/dashboard");
    } catch (err) {
      navigate("/dashboard");
    }
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/auth/complete-onboarding`);
      setUser(prev => ({ ...prev, onboarding_complete: true }));
    } catch (err) {}
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4" data-testid="onboarding-page">
      <div className="w-full max-w-[480px]">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`h-1.5 flex-1 transition-colors ${s <= step ? 'bg-[#1A3DFF]' : 'bg-[#E5E7EB]'}`} />
            </div>
          ))}
        </div>

        {/* Step 1: Business Info */}
        {step === 1 && (
          <div data-testid="onboarding-step-1">
            <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center mb-6">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-[32px] tracking-[-0.03em] text-[#0A0A0A] mb-2">
              Quick setup
            </h1>
            <p className="text-[15px] text-[#6B7280] mb-1">
              This will get you sending invoices in under a minute.
            </p>
            <p className="text-[13px] text-[#9CA3AF] mb-8">
              For the full suite and best experience, visit Settings after — it's a one-time setup, takes 5 minutes.
            </p>

            <div className="space-y-5">
              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Business name *</label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => {
                    setBusinessName(e.target.value);
                    if (!businessInitials) {
                      const auto = e.target.value.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                      setBusinessInitials(auto);
                    }
                  }}
                  className="w-full border border-[#E5E7EB] px-4 py-3 text-[15px] focus:outline-none focus:border-[#1A3DFF]"
                  placeholder="Acme Studio"
                  autoFocus
                  data-testid="onboarding-business-name"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Initials (for logo)</label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1A3DFF] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-[14px]">{businessInitials || "VB"}</span>
                  </div>
                  <input
                    type="text"
                    value={businessInitials}
                    onChange={(e) => setBusinessInitials(e.target.value.toUpperCase().slice(0, 3))}
                    className="flex-1 border border-[#E5E7EB] px-4 py-3 text-[15px] focus:outline-none focus:border-[#1A3DFF] uppercase"
                    placeholder="AS"
                    maxLength={3}
                    data-testid="onboarding-initials"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Business address</label>
                <textarea
                  value={businessAddress}
                  onChange={(e) => setBusinessAddress(e.target.value)}
                  className="w-full border border-[#E5E7EB] px-4 py-3 text-[15px] focus:outline-none focus:border-[#1A3DFF] h-20 resize-none"
                  placeholder="123 Main St, London, UK"
                  data-testid="onboarding-address"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Reply-to email</label>
                <p className="text-[12px] text-[#9CA3AF] mb-1.5">When clients reply to invoice emails, where should they go?</p>
                <input
                  type="email"
                  value={replyEmail}
                  onChange={(e) => setReplyEmail(e.target.value)}
                  className="w-full border border-[#E5E7EB] px-4 py-3 text-[15px] focus:outline-none focus:border-[#1A3DFF]"
                  placeholder="you@yourbusiness.com"
                  data-testid="onboarding-reply-email"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-8">
              <button
                onClick={handleSkip}
                className="text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
                data-testid="onboarding-skip"
              >
                Skip for now
              </button>
              <button
                onClick={handleStep1}
                disabled={!businessName.trim() || saving}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
                data-testid="onboarding-next-1"
              >
                {saving ? "Saving..." : "Next"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Create First Invoice */}
        {step === 2 && (
          <div data-testid="onboarding-step-2">
            <div className="w-12 h-12 bg-[#1A3DFF] flex items-center justify-center mb-6">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-[32px] tracking-[-0.03em] text-[#0A0A0A] mb-2">
              Create your first invoice
            </h1>
            <p className="text-[15px] text-[#6B7280] mb-8">
              It takes about 30 seconds. Add a client, line items, and you're done.
            </p>

            {/* Preview card */}
            <div className="bg-white border border-[#E5E7EB] p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-[#1A3DFF] flex items-center justify-center">
                  <span className="text-white font-bold text-[10px]">{businessInitials || "VB"}</span>
                </div>
                <span className="font-semibold text-[15px] text-[#0A0A0A]">{businessName || "Your Business"}</span>
              </div>
              <div className="h-px bg-[#E5E7EB] my-4" />
              <div className="space-y-2">
                <div className="h-3 bg-[#F3F4F6] w-3/4 rounded-sm" />
                <div className="h-3 bg-[#F3F4F6] w-1/2 rounded-sm" />
                <div className="h-3 bg-[#F3F4F6] w-2/3 rounded-sm" />
              </div>
              <div className="flex justify-end mt-4">
                <div className="h-4 bg-[#F3F4F6] w-24 rounded-sm" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(3)}
                className="text-[14px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
                data-testid="onboarding-skip-2"
              >
                I'll do this later
              </button>
              <button
                onClick={handleCreateInvoice}
                className="btn-primary flex items-center gap-2"
                data-testid="onboarding-create-invoice"
              >
                Create invoice
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: All Set */}
        {step === 3 && (
          <div className="text-center" data-testid="onboarding-step-3">
            <div className="w-16 h-16 bg-[#ECFDF5] flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#059669]" />
            </div>
            <h1 className="font-display text-[32px] tracking-[-0.03em] text-[#0A0A0A] mb-2">
              You're ready to invoice!
            </h1>
            <p className="text-[15px] text-[#6B7280] mb-6">
              Your basic setup is complete. You can start sending invoices right away.
            </p>

            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-6 text-left mx-auto max-w-[360px]">
              <p className="text-[13px] font-semibold text-[#0A0A0A] mb-2">Next steps</p>
              <div className="space-y-2 text-[13px] text-[#6B7280]">
                <p className="flex gap-2">
                  <span className="text-[#1A3DFF] font-bold flex-shrink-0">1.</span>
                  Visit <strong className="text-[#0A0A0A]">Settings</strong> for full setup — payment methods, tax, branding, email. One-time, 5 minutes.
                </p>
                <p className="flex gap-2">
                  <span className="text-[#1A3DFF] font-bold flex-shrink-0">2.</span>
                  Need help? Open the <strong className="text-[#0A0A0A]">Getting Started</strong> panel anytime for guidance.
                </p>
              </div>
            </div>

            <button
              onClick={handleComplete}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="onboarding-complete"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
